import { Box, Stack } from '@mui/material';
import * as React from 'react';

import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { PresentationCoverSlideConfig } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import { CustomerThemeProvider } from '@/styles/themes/CustomerThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import { isPrintPath } from '@/utils/isPrintPath';

import { PresentationTypographyProvider } from '../clientPresentation/PresentationTypographyProvider';
import {
  ScalableSlide,
  ScalableSlideSizing,
} from '../ScalableSlide/ScalableSlide';
import {
  PDF_SERVICE_VIEWPORT_WIDTH_PX,
  US_LETTER_ASPECT_RATIO,
  US_LETTER_SCALE_WIDTH_PX,
} from './PresentationSlide.constants';
import { usePresentationSlideStageProps } from './PresentationSlideStage.context';
import { SlideWatermark, WATERMARK_SELECTOR } from './SlideWatermark';

interface HeaderProps {
  leftHeaderContent?: React.ReactNode;
  rightHeaderContent?: React.ReactNode;
}

export function PresentationSlideHeader({
  leftHeaderContent,
  rightHeaderContent,
}: HeaderProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      justifyContent="space-between"
      component="header"
      sx={{ py: 2, borderBottom: `1px solid ${COLORS.NEUTRAL_GRAY[200]}` }}
    >
      {leftHeaderContent}
      {rightHeaderContent && rightHeaderContent}
    </Stack>
  );
}

export type PresentationSlideProps = React.PropsWithChildren<{
  footer?: React.ReactNode;
  // this id property is used to make it so that the slide can be scrolled to
  id: string;
  /**
   * readyToScale is an optional property that can be used to delay the scaling of the slide
   * which may be useful if the slide needs to be initialized before it can be scaled correctly
   */
  readyToScale?: boolean;
  sizing?: ScalableSlideSizing;
  /** Background color for the entire slide (including behind footer). */
  slideBackgroundColor?: string | null;
  /** Background image URL for the entire slide. */
  slideBackgroundImage?: string | null;
  /** Opacity (0-100) for the slide background layers. */
  slideBackgroundAlpha?: number | null;
  /**
   * Drag-driven origin offsets for the background image (T1-5959). In
   * slide-coordinate pixels relative to the centered cover position;
   * null/undefined treated as 0.
   */
  slideBackgroundOriginX?: number | null;
  slideBackgroundOriginY?: number | null;
  /**
   * Background-image scale, expressed as a 50–150 percentage (T1-5959).
   * 100 (or null/undefined) keeps the existing cover behavior; values
   * other than 100 grow/shrink the background-painting box around the
   * slide center, with the slide's overflow:hidden cropping anything
   * past 100%.
   */
  slideBackgroundScale?: number | null;
  slideDesign?: PresentationCoverSlideConfig;
  /** Text to show diagonally behind the slide content, such as "DRAFT". */
  watermark?: string;
}> &
  HeaderProps &
  Omit<React.HTMLProps<HTMLDivElement>, 'ref'>;

export function PresentationSlide({
  rightHeaderContent,
  leftHeaderContent,
  children,
  footer,
  readyToScale,
  sizing,
  slideBackgroundColor,
  slideBackgroundImage,
  slideBackgroundAlpha,
  slideBackgroundOriginX,
  slideBackgroundOriginY,
  slideBackgroundScale,
  slideDesign,
  watermark,
  ...htmlProps
}: PresentationSlideProps) {
  const stageProps = usePresentationSlideStageProps(htmlProps.id);
  const usesIntrinsicDimensions =
    sizing === 'intrinsic' || sizing === 'fit-content';

  // In the print path, normal slides render at 1375px then scale-transform to
  // 1500px. The export slide has no transform, so use the full viewport width
  // to match. In the designer, use the pre-transform width to match other slides.
  const intrinsicSlideWidth = isPrintPath()
    ? PDF_SERVICE_VIEWPORT_WIDTH_PX
    : US_LETTER_SCALE_WIDTH_PX;

  // Minimum height matching a standard page so the footer anchors to the
  // bottom even when content is shorter than one page.
  const intrinsicMinHeight = intrinsicSlideWidth / US_LETTER_ASPECT_RATIO;

  const hasBackground = slideBackgroundColor || slideBackgroundImage;
  const bgOpacity = (slideBackgroundAlpha ?? 100) / 100;
  const bgOriginX = slideBackgroundOriginX ?? 0;
  const bgOriginY = slideBackgroundOriginY ?? 0;
  // Scale grows or shrinks the ::before that paints the background. 100
  // keeps it at the slide bounds; >100 makes it larger and the slide's
  // overflow:hidden crops it; <100 reveals the slide background color
  // around the edges.
  const bgScale = (slideBackgroundScale ?? 100) / 100;
  const bgInset = `${(1 - bgScale) * 50}%`;
  const gradient = slideDesign?.backgroundGradientEndColor
    ? `linear-gradient(${slideDesign.backgroundGradientAngle ?? 90}deg, ${slideBackgroundColor ?? '#FFFFFF'}, ${slideDesign.backgroundGradientEndColor})`
    : null;
  const backgroundLayers = [
    slideBackgroundImage
      ? `url(${JSON.stringify(slideBackgroundImage)})`
      : null,
    gradient,
  ]
    .filter(Boolean)
    .join(', ');
  const overlaySize = (slideDesign?.backgroundOverlayPercentScale ?? 100) / 2;
  const hasCustomBackground = Boolean(
    hasBackground || gradient || slideDesign?.backgroundOverlayPath
  );

  return (
    <CustomerThemeProvider>
      <PresentationTypographyProvider>
        <TextEditorProvider theme="presentationContent">
          <ScalableSlide
            aspectRatio={US_LETTER_ASPECT_RATIO}
            scaleWidthPx={US_LETTER_SCALE_WIDTH_PX}
            readyToScale={readyToScale}
            sizing={sizing}
            data-presentation-slide-id={htmlProps.id}
          >
            <Stack
              spacing={2}
              justifyContent="space-between"
              height={usesIntrinsicDimensions ? undefined : '100%'}
              minHeight={
                usesIntrinsicDimensions ? intrinsicMinHeight : undefined
              }
              minWidth={
                usesIntrinsicDimensions ? intrinsicSlideWidth : undefined
              }
              sx={{
                ...((hasCustomBackground || watermark) && {
                  position: 'relative',
                  isolation: 'isolate',
                  // Only crop when the ::before is actually larger than
                  // the slide — at scale 1 the inset is 0 and there's
                  // nothing to clip, so we leave overflow alone to
                  // avoid changing layout for every existing slide
                  // that has a background but no scale override.
                  ...((bgScale !== 1 || slideDesign?.backgroundOverlayPath) && {
                    overflow: 'hidden',
                  }),
                  // Render backgrounds via ::before so opacity only
                  // affects the background, not the slide content.
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: bgInset,
                    opacity: bgOpacity,
                    ...(slideBackgroundColor && {
                      backgroundColor: slideBackgroundColor,
                    }),
                    ...(backgroundLayers && {
                      backgroundImage: backgroundLayers,
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      // T1-5959: origin offsets shift the image from its
                      // centered position; the slide already clips overflow.
                      backgroundPosition: `calc(50% + ${bgOriginX}px) calc(50% + ${bgOriginY}px)`,
                    }),
                  },
                  ...(slideDesign?.backgroundOverlayPath && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      zIndex: 0,
                      pointerEvents: 'none',
                      left: `${slideDesign.backgroundOverlayOriginX ?? 50}%`,
                      top: `${slideDesign.backgroundOverlayOriginY ?? 50}%`,
                      width: `${overlaySize}%`,
                      height: `${overlaySize}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundImage: `url(${JSON.stringify(slideDesign.backgroundOverlayPath)})`,
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      opacity: slideDesign.backgroundOverlayAlpha ?? 0.2,
                    },
                  }),
                  // Ensure content renders above the ::before layer. The
                  // watermark sets its own position.
                  [`& > *:not(${WATERMARK_SELECTOR})`]: {
                    position: 'relative',
                    zIndex: 1,
                  },
                }),
              }}
              {...htmlProps}
              {...stageProps}
            >
              {(leftHeaderContent || rightHeaderContent) && (
                <Box
                  px={2}
                  maxWidth={
                    usesIntrinsicDimensions ? intrinsicSlideWidth : undefined
                  }
                >
                  <PresentationSlideHeader
                    rightHeaderContent={rightHeaderContent}
                    leftHeaderContent={leftHeaderContent}
                  />
                </Box>
              )}
              <Box
                flexGrow={1}
                minHeight={0}
                display="flex"
                flexDirection="column"
              >
                {children}
              </Box>
              {footer && (
                <Box
                  pb={2}
                  maxWidth={
                    usesIntrinsicDimensions ? intrinsicSlideWidth : undefined
                  }
                >
                  {footer}
                </Box>
              )}
              {/* Keep this last: Stack spacing adds a top margin to each child
                  after the first, so a first-child watermark would move the
                  header down. */}
              {watermark && (
                <SlideWatermark
                  text={watermark}
                  aboveContent={!hasCustomBackground}
                />
              )}
            </Stack>
          </ScalableSlide>
        </TextEditorProvider>
      </PresentationTypographyProvider>
    </CustomerThemeProvider>
  );
}
